// Chaotic scattered background of batrat + usdc coins littering every section.
// Positions are pseudo-random but deterministic per instance so layout is stable.
import { useMemo } from "react";
import { motion } from "framer-motion";

const BATRAT = "/img/batrat.jpg";
const USDC = "/img/usdc.png";

function seedRand(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export default function ScatterBackground({
  count = 42,
  seed = 7,
  className = "",
  parallax = true,
}) {
  const items = useMemo(() => {
    const rand = seedRand(seed);
    const out = [];
    for (let i = 0; i < count; i++) {
      const isBat = rand() > 0.42; // more batrats than coins
      const size = isBat ? 60 + rand() * 220 : 40 + rand() * 140;
      out.push({
        id: i,
        isBat,
        top: rand() * 100,
        left: rand() * 100,
        rot: (rand() - 0.5) * 90,
        size,
        opacity: 0.35 + rand() * 0.55,
        depth: rand() * 40 - 20, // for parallax
        z: Math.floor(rand() * 3), // stacking
      });
    }
    return out;
  }, [count, seed]);

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      data-testid="scatter-bg"
      aria-hidden="true"
    >
      {items.map((it) => (
        <motion.img
          key={it.id}
          src={it.isBat ? BATRAT : USDC}
          alt=""
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: it.opacity, scale: 1 }}
          transition={{
            duration: 0.9,
            delay: (it.id % 12) * 0.03,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{
            position: "absolute",
            top: `${it.top}%`,
            left: `${it.left}%`,
            width: `${it.size}px`,
            height: `${it.size}px`,
            objectFit: "contain",
            transform: `translate(-50%, -50%) rotate(${it.rot}deg)`,
            mixBlendMode: it.isBat ? "multiply" : "normal",
            zIndex: it.z,
            filter: it.isBat
              ? "contrast(1.05) saturate(0.95)"
              : "drop-shadow(0 6px 0 rgba(0,0,0,0.15))",
            willChange: parallax ? "transform" : "auto",
          }}
        />
      ))}
    </div>
  );
}
